import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiUserCheck } from 'react-icons/fi';
import { authAPI, setTokens } from '../services/api';
import { fetchCurrentUser } from '../store/slices/authSlice';

/**
 * Opened in a new tab by the tenant owner's "Login as" action.
 * Consumes the one-time handoff token, stores the resulting tokens in
 * sessionStorage (per-tab) so the owner's localStorage session in the
 * original tab is not affected, then enters the dashboard.
 */
function ImpersonationHandoff() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState(null);
  // Guards against React 18 StrictMode double-invoke in dev — the backend
  // flags the handoff token as used on first call, and a second call would
  // 401 ("already used") + trigger the axios refresh path with the owner's
  // localStorage token.
  const consumedRef = useRef(false);

  useEffect(() => {
    const handoffToken = params.get('token');
    if (!handoffToken) {
      setError(t('impersonation.missingToken'));
      return;
    }
    if (consumedRef.current) return;
    consumedRef.current = true;

    // Strip the token from the visible URL / browser history so it can't leak
    // via Referer on later subresource loads or be replayed from history.
    try {
      window.history.replaceState({}, '', '/impersonate/handoff');
    } catch { /* ignore — non-fatal */ }

    // Intentionally no `cancelled` flag: the ref guarantees this runs once,
    // and adding a cleanup flag would discard the only in-flight consume
    // during StrictMode's dev mount→cleanup→mount cycle, leaving an orphan
    // impersonation session on the backend and a stuck spinner on the UI.
    (async () => {
      try {
        const res = await authAPI.consumeImpersonation(handoffToken);
        setTokens(
          { accessToken: res.data.accessToken, refreshToken: res.data.refreshToken },
          { impersonation: true }
        );
        // unwrap() throws if the thunk rejected — otherwise we'd silently
        // navigate to /dashboard with isAuthenticated=false and PrivateRoute
        // would bounce the user to /login.
        await dispatch(fetchCurrentUser()).unwrap();
        navigate('/dashboard', { replace: true });
      } catch (err) {
        setError(err.response?.data?.error || err.message || t('impersonation.failed'));
      }
    })();
  }, [dispatch, navigate, params, t]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f8fafc',
      padding: '24px',
    }}>
      <div style={{
        maxWidth: 420,
        width: '100%',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: '32px 28px',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          margin: '0 auto 16px',
          background: error ? '#fef2f2' : '#eef2ff',
          color: error ? '#ef4444' : '#6366f1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {error ? <FiAlertCircle size={24} /> : <FiUserCheck size={24} />}
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}>
          {error ? t('impersonation.failedTitle') : t('impersonation.signingIn')}
        </h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem', lineHeight: 1.5 }}>
          {error || t('impersonation.signingInDesc')}
        </p>
        {error && (
          <button
            type="button"
            onClick={() => window.close()}
            style={{
              marginTop: 20,
              padding: '9px 20px',
              borderRadius: 8,
              background: '#111827',
              color: '#fff',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('common.close')}
          </button>
        )}
      </div>
    </div>
  );
}

export default ImpersonationHandoff;
